<template>
  <main class="home">
    <div class="hero-grid">
      <section class="hero">
        <p class="eyebrow">// développeur</p>
        <h1>Alexis Mayeur</h1>
        <p class="tagline">
          Je conçois et développe des projets logiciels, du prototype
          expérimental à l'application complète.
        </p>
        <div class="cta-row">
          <router-link to="/projets" class="btn btn-primary">Voir mes projets</router-link>
        </div>
      </section>

      <aside class="hero-media" v-if="featured && featured.screenshots.length">
        <a
          class="preview"
          :href="featured.url"
          target="_blank"
          rel="noopener"
        >
          <transition name="fade" mode="out-in">
            <img
              :key="activeIndex"
              :src="featured.screenshots[activeIndex]"
              :alt="`Capture d'écran ${featured.name}`"
            >
          </transition>
          <div class="preview-overlay">
            <p class="preview-title">
              {{ featured.name }}
              <span v-if="version" class="preview-version">v{{ version }}</span>
            </p>
            <p class="preview-description">{{ featured.description }}</p>
            <span class="preview-cta">Visiter le site ↗</span>
          </div>
        </a>
        <div class="thumbs">
          <button
            v-for="(shot, i) in featured.screenshots"
            :key="shot"
            type="button"
            class="thumb"
            :class="{ active: i === activeIndex }"
            @click="activeIndex = i"
          >
            <img :src="shot" :alt="`Miniature ${i + 1}`">
          </button>
        </div>
      </aside>
    </div>
  </main>
</template>

<script>
import { getProjects } from '@/services/api'

export default {
  name: 'HomeView',
  data () {
    return {
      activeIndex: 0,
      version: null,
      featured: null
    }
  },
  async created () {
    try {
      const projects = await getProjects()
      this.featured = projects.find(p => p.featured) || projects[0] || null
      if (this.featured && this.featured.versionUrl) {
        this.fetchVersion(this.featured.versionUrl)
      }
    } catch (e) {
      // pas de projet mis en avant si l'API est injoignable
    }
  },
  methods: {
    async fetchVersion (versionUrl) {
      try {
        const res = await fetch(versionUrl)
        const text = await res.text()
        const match = text.match(/^version:\s*(.+)$/m)
        if (match) this.version = match[1].trim()
      } catch (e) {
        // version indisponible, on affiche l'aperçu sans badge
      }
    }
  }
}
</script>

<style scoped>
.home {
  flex: 1;
  display: flex;
  align-items: center;
  padding: 8vh 6vw;
}

.hero-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  align-items: center;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
}

@media (min-width: 900px) {
  .hero-grid {
    grid-template-columns: minmax(0, 1fr) minmax(360px, 620px);
  }
}

.hero {
  max-width: 640px;
  text-align: left;
}

.eyebrow {
  font-family: 'Consolas', 'Courier New', monospace;
  color: var(--accent);
  margin: 0 0 8px;
  font-size: 0.9rem;
  opacity: 0;
  animation: fade-in-up 0.5s ease 0.05s both;
}

.eyebrow::after {
  content: '';
  display: inline-block;
  width: 8px;
  height: 1em;
  margin-left: 4px;
  background: var(--accent);
  vertical-align: text-bottom;
  animation: blink 1s step-end infinite;
}

.hero h1 {
  font-size: clamp(2.4rem, 6vw, 4rem);
  margin: 0 0 16px;
  opacity: 0;
  animation: fade-in-up 0.5s ease 0.15s both;
}

.tagline {
  color: var(--text-dim);
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 0 0 32px;
  opacity: 0;
  animation: fade-in-up 0.5s ease 0.25s both;
}

.cta-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  opacity: 0;
  animation: fade-in-up 0.5s ease 0.35s both;
}

.btn {
  display: inline-block;
  padding: 12px 24px;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 600;
  transition: transform 0.15s ease, background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.btn-primary {
  background: var(--accent);
  color: #0d1117;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px -8px rgba(63, 185, 80, 0.6);
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.hero-media {
  opacity: 0;
  animation: fade-in-up 0.6s ease 0.3s both;
}

.preview {
  position: relative;
  display: block;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  background: #000;
  aspect-ratio: 16 / 10;
  margin-bottom: 12px;
  box-shadow: 0 24px 48px -24px rgba(0, 0, 0, 0.6);
  cursor: pointer;
}

.preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.preview-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  text-align: center;
  background: rgba(13, 17, 23, 0.9);
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.preview:hover .preview-overlay,
.preview:focus-visible .preview-overlay {
  opacity: 1;
  transform: translateY(0);
}

.preview-title {
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: 10px;
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--text);
}

.preview-version {
  font-size: 0.85rem;
  font-weight: 400;
  color: var(--text-dim);
}

.preview-description {
  margin: 0;
  max-width: 380px;
  color: var(--text-dim);
  line-height: 1.5;
}

.preview-cta {
  margin-top: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--accent);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.thumbs {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.thumb {
  width: 88px;
  height: 55px;
  padding: 0;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid transparent;
  cursor: pointer;
  opacity: 0.6;
  background: none;
  transition: opacity 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.thumb:hover {
  opacity: 1;
  transform: translateY(-2px);
}

.thumb.active {
  opacity: 1;
  border-color: var(--accent);
}
</style>
